import React from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import { BsPinAngleFill } from "react-icons/bs";
import { markAsRead } from "../services/userNotificationService";

const NOTIF_TYPE_ICONS = {
  new_feature: "🆕",
  update: "🔄",
  maintenance: "🛠️",
  event: "🎉",
  announcement: "📢",
};

function formatTimeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.floor((now - d) / 1000);

  if (diff < 60) return "Vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} ngày trước`;
  return d.toLocaleDateString("vi-VN");
}

const NotificationPopup = ({
  notifications = [],
  readIds = [],
  loading = false,
  onMarkRead,
  onClose,
  style,
  popupRef
}) => {
  const navigate = useNavigate();
  const readSet = new Set(readIds);
  const userId = localStorage.getItem("userId");

  const handleClick = async (notif) => {
    if (!readSet.has(notif.id)) {
      await markAsRead(userId, notif.id);
      onMarkRead?.(notif.id);
    }
    onClose?.();
    navigate("/notifications", { state: { selectedId: notif.id } });
  };

  const handleViewAll = () => {
    onClose?.();
    navigate("/notifications");
  };

  return (
    <div ref={popupRef} className="fixed bg-white rounded-2xl border border-gray-100 z-[9999] overflow-hidden flex flex-col"
      style={{ ...style, maxHeight: "480px", boxShadow: "0 10px 40px -10px rgba(0,0,0,0.1), 0 4px 10px -4px rgba(0,0,0,0.05)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50 bg-white">
        <h3 className="text-base font-semibold text-gray-800">Thông báo</h3>
        <span className="text-xs text-gray-400">
          {notifications.length} thông báo
        </span>
      </div>

      {/* Content */}
      <div className="overflow-y-auto max-h-[360px] divide-y divide-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Đang tải...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-sm text-gray-500 text-center">
              Chưa có thông báo nào
            </p>
          </div>
        ) : (
          notifications.map((notif) => {
            const isRead = readSet.has(notif.id);
            const typeIcon = NOTIF_TYPE_ICONS[notif.type] || NOTIF_TYPE_ICONS.announcement;
            return (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full text-left px-5 py-3.5 transition-all duration-150 flex items-start gap-3 group ${
                  !isRead ? "bg-blue-50/20 hover:bg-blue-50/40" : "bg-white hover:bg-gray-50"
                }`}
              >
                <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-base">
                  {typeIcon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {notif.isPinned && (
                      <BsPinAngleFill size={12} className="text-amber-500 flex-shrink-0" />
                    )}
                    <p
                      className={`text-sm leading-snug line-clamp-2 ${
                        isRead
                          ? "text-gray-600 font-normal"
                          : "text-gray-900 font-medium"
                      }`}
                    >
                      {notif.title}
                    </p>
                  </div>
                  {notif.description && (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                      {notif.description.replace(/<[^>]*>/g, "").substring(0, 80)}
                    </p>
                  )}
                  <span className="text-[11px] text-gray-400 mt-1 block">
                    {formatTimeAgo(notif.createdAt)}
                  </span>
                </div>
                {!isRead && (
                  <div className="mt-2 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-blue-500 shadow-sm shadow-blue-200"></div>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100">
        <button
          onClick={handleViewAll}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium text-blue-500 hover:text-blue-600 hover:bg-gray-50 transition-colors duration-150 bg-white"
        >
          Xem tất cả thông báo
          <FiArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default NotificationPopup;
